/**
 * A tested village plan in everyday words — the card's title and the line under it (village rebuild,
 * the agreed mock-up's plans screen, owner 20–21 Oct 2026).
 *
 * The engine's names ("Sell your home, start a super pension and invest the rest") were written for an
 * adviser's list. The card says what happens to the home, where the entry payment comes from, and where
 * what is left goes — with the amounts and the accounts' own names ("the ANZ account, the MLC and Hub24
 * pensions"), from the candidate's inputs. Presentation only: nothing here is computed beyond sums.
 */
import { formatCurrency } from '../../utils/index.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { investedWhereWords } from '../../utils/investedWhere.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

export interface RvCandidateLike {
    id?: string;
    name?: string;
    inputs?: Record<string, unknown> | null;
    fundingBreakdown?: Array<{ label: string; amount: number }>;
    [key: string]: unknown;
}

const num = (v: unknown) => Number(v) || 0;
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

const KIND_WORDS: Record<string, { one: string; many: string; plain: string }> = {
    bankAccounts: { one: 'account', many: 'accounts', plain: 'the bank account' },
    termDeposits: { one: 'term deposit', many: 'term deposits', plain: 'the term deposit' },
    accountBasedPensions: { one: 'pension', many: 'pensions', plain: 'the account-based pension' },
    shares: { one: 'shares', many: 'shares', plain: 'the shares' },
};

/** "the ANZ account, the MLC and Hub24 pensions and the shares" — the accounts an entry draw takes from. */
export function rvAccountsWords(funding: Record<string, unknown> | null | undefined, factFind: DraftFactFind): string {
    if (!funding) return '';
    const assets = (factFind?.assets || {}) as Record<string, Array<{ id?: unknown; description?: string }> | undefined>;
    const groups: string[] = [];
    for (const kind of ['bankAccounts', 'termDeposits', 'accountBasedPensions', 'shares']) {
        const names: string[] = [];
        let unnamed = 0;
        for (const [key, amount] of Object.entries(funding)) {
            if (!(num(amount) > 0) || !key.startsWith(`${kind}-`)) continue;
            const id = key.slice(kind.length + 1);
            const asset = (assets[kind] || []).find((a) => String(a?.id) === id);
            const desc = String(asset?.description || '').trim();
            if (desc && kind !== 'shares') names.push(desc); else unnamed += 1;
        }
        if (!names.length && !unnamed) continue;
        const w = KIND_WORDS[kind]!;
        if (kind === 'shares') { groups.push('the shares'); continue; }
        if (!names.length) { groups.push(unnamed > 1 ? `the ${w.many}` : w.plain); continue; }
        const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
        groups.push(`the ${list} ${names.length + unnamed > 1 ? w.many : w.one}`);
    }
    if (groups.length <= 1) return groups[0] || '';
    return `${groups.slice(0, -1).join(', ')} and ${groups[groups.length - 1]}`;
}

/*
 * HOW MUCH FROM EACH, IN THE ORDER DRAWN (decision 10, 21 Oct 2026). With every way of drawing the money now a
 * plan of its own, two plans can take from the same accounts in different amounts, so the line says the
 * amounts: "$130,000 of the entry payment comes from the ANZ account, $320,000 from the MLC pension and
 * $200,000 from the Hub24 pension". The funding map is built in the order it is drawn.
 */
export function rvDrawWords(funding: Record<string, unknown> | null | undefined, factFind: DraftFactFind, what = 'the entry payment'): string {
    if (!funding) return '';
    const assets = (factFind?.assets || {}) as Record<string, Array<{ id?: unknown; description?: string }> | undefined>;
    const parts: string[] = [];
    for (const [key, amount] of Object.entries(funding)) {
        if (!(num(amount) > 0)) continue;
        const kind = Object.keys(KIND_WORDS).find((k) => key.startsWith(`${k}-`));
        if (!kind) continue;
        const asset = (assets[kind] || []).find((a) => String(a?.id) === key.slice(kind.length + 1));
        const desc = String(asset?.description || '').trim();
        const name = kind === 'shares' || !desc ? KIND_WORDS[kind]!.plain : `the ${desc} ${KIND_WORDS[kind]!.one}`;
        parts.push(`${formatCurrency(Math.round(num(amount)))} from ${name}`);
    }
    if (!parts.length) return '';
    const [first, ...rest] = parts;
    const lead = first!.replace(/ from /, ` of ${what} comes from `);
    return rest.length ? `${lead}, ${joinAnd(rest)}` : lead;
}

/** What a sale's leftover is put to, as short phrases in the order the card says them. */
function usesOf(d: Record<string, unknown>, factFind: DraftFactFind): { short: string[]; long: string[] } {
    const v = householdVoice(factFind);
    const clientName = factFind?.client?.firstName || 'you';
    const partnerName = factFind?.partner?.firstName || 'your partner';
    const pension = d.commenceIncomeStream === true;
    const ds = num(d.downsizerContribution);
    const pds = num(d.partnerDownsizerContribution);
    const short: string[] = [];
    const long: string[] = [];
    if (ds + pds > 0) {
        short.push(`move ${formatCurrency(ds + pds)} into super${pension ? ' pensions' : ''}`);
        const into = ds > 0 && pds > 0
            ? `${formatCurrency(ds)} into ${clientName}'s super and ${formatCurrency(pds)} into ${partnerName}'s, each as a downsizer contribution`
            : `${formatCurrency(ds + pds)} into ${ds > 0 ? clientName : partnerName}'s super as a downsizer contribution`;
        long.push(`${into}${pension ? ' drawn as a pension' : ' left to grow in super'}`);
    }
    const annuity = num(d.purchaseAnnuity);
    if (annuity > 0) {
        // Each type said by what the family receives (guaranteed income review, 21 Oct 2026); no product named.
        const back = String(d.rvAnnuityType) === 'guaranteed'
            ? 'that pays the full amount back to the family at any time'
            : num(d.rvAnnuityResidualPercent) > 0 ? 'that pays the money back to the family in the early years' : 'that pays the most income, with nothing back to the family';
        short.push(`put ${formatCurrency(annuity)} into a guaranteed income`);
        long.push(`${formatCurrency(annuity)} into a guaranteed income for life ${back}`);
    }
    const ip = num(d.purchaseInvestmentProperty);
    if (ip > 0) {
        short.push('buy an investment property');
        long.push(`${formatCurrency(ip)} on an investment property${num(d.investmentPropertyRent) > 0 ? `, rented for about ${formatCurrency(num(d.investmentPropertyRent))} a year` : ''}`);
    }
    const gift = num(d.giftingAmount);
    if (gift > 0) { short.push(`give ${formatCurrency(gift)} to family`); long.push(`${formatCurrency(gift)} given to family`); }
    const bond = num(d.funeralBondPurchase);
    if (bond > 0) { short.push('put some in a funeral bond'); long.push(`${formatCurrency(bond)} into a funeral bond`); }
    const shares = num(d.purchaseShares);
    if (shares > 0) {
        short.push(short.length ? 'invest the rest' : 'invest what is left');
        long.push(`${short.length > 1 ? 'the other ' : ''}${formatCurrency(shares)} invested ${investedWhereWords(factFind)}${v.couple ? `, in ${v.you ? 'your' : 'their'} joint names` : ''}`);
    }
    return { short, long };
}

/** The card's title and sub-line for a tested plan. */
export function rvPlanWords(row: RvCandidateLike, factFind: DraftFactFind, entryFee: number): { title: string; sub: string } {
    const inputs = (row.inputs || {}) as Record<string, unknown>;
    const id = String(row.id || '');
    const fee = formatCurrency(Math.round(entryFee));
    const fundFrom = String(inputs.fundFrom || 'liquid');
    const debts = /-clear-debt$/.test(id) ? ' Your debts are cleared at the same time.' : '';
    const debtsTitle = /-clear-debt$/.test(id) ? ', and clear the debts' : '';
    const funding = (inputs.entryFeeFundingOverride || null) as Record<string, unknown> | null;
    const fromOldUnit = (row.fundingBreakdown || []).filter((f) => /current village unit/i.test(f.label)).reduce((s, f) => s + num(f.amount), 0);
    const oldUnitWords = fromOldUnit > 0 ? ` ${formatCurrency(fromOldUnit)} of it is the payout from the current village unit.` : '';
    const drawn = rvDrawWords(funding, factFind);
    const hasHome = num(factFind?.home?.value) > 0;

    if (fundFrom === 'liquid') {
        const rented = inputs.rentHome === true;
        const rent = num((inputs.rentDetails as { rentAmount?: unknown } | undefined)?.rentAmount);
        const title = rented
            ? `Keep the home and rent it out; pay the ${fee} from savings and investments${debtsTitle}`
            : hasHome
                ? `Keep the home as it is; pay the ${fee} from savings and investments${debtsTitle}`
                : `Pay the ${fee} from savings and investments${debtsTitle}`;
        const sub = `${drawn ? `${drawn.replace(/^./, (c) => c.toUpperCase())}.` : 'The entry payment comes from savings.'}${oldUnitWords}${rented && rent > 0 ? ` The home is let at ${formatCurrency(Math.round(rent))} a week, after the weeks it sits empty.` : ''}${debts}`;
        return { title, sub: sub.trim() };
    }

    if (fundFrom === 'investment_property') {
        const props = (factFind?.assets as { investmentProperty?: Array<{ id?: unknown; description?: string; address?: string }> } | undefined)?.investmentProperty || [];
        const sel = (inputs.investmentPropertyDetails as { selectedPropertyId?: unknown } | undefined)?.selectedPropertyId;
        const p = props.find((x) => String(x?.id) === String(sel)) || props[0];
        const what = String(p?.description || p?.address || '').trim();
        return {
            title: `Sell the investment property${what ? ` (${what})` : ''} to pay the ${fee}${debtsTitle}`,
            sub: `The sale pays the entry payment, after selling costs and capital gains tax.${hasHome ? ' The home is kept as it is.' : ''}${oldUnitWords}${debts}`.trim(),
        };
    }

    // Selling the home.
    const d = (inputs.sellHomeDetails || {}) as Record<string, unknown>;
    const left = Math.max(0, num(d.salePrice) - num(d.sellingCosts) - entryFee);
    const { short, long } = usesOf(d, factFind);
    const title = short.length ? `Sell the home, ${joinAnd(short)}${debtsTitle}` : `Sell the home and keep what is left in the bank${debtsTitle}`;
    const sub = long.length
        ? `The sale pays the ${fee} entry payment. Of the ${formatCurrency(Math.round(left))} left: ${long.join('; ')}.${debts}`
        : `The sale pays the ${fee} entry payment. The ${formatCurrency(Math.round(left))} left stays in the bank account.${debts}`;
    return { title, sub };
}

/** The money a plan moves into the village and beyond, for the card's second bar ("Paid into the village"). */
export function rvPaidIn(entryFee: number): { amount: number; label: string } {
    return { amount: Math.max(0, entryFee), label: 'Paid into the village' };
}
